/**
 * Textarea Atom
 * @layer atom
 * @package @plerity/ui-core
 */

import { forwardRef, useId } from "react";
import { cn } from "../utils/cn";
import type { TextareaProps } from "./Textarea.types";
import { textareaVariants } from "./Textarea.variants";

// Re-exported so `import { textareaVariants } from "@plerity/ui-core"` (and the
// component barrel `./Textarea`) keep working after the leaf-module extraction.
export { textareaVariants };

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, variant, size, label, error, helperText, id, rows = 3, ...props }, ref) => {
    const generatedId = useId();
    const textareaId = id ?? generatedId;
    const effectiveVariant = error ? "error" : variant;

    return (
      <div className="space-y-1">
        {label && (
          <label htmlFor={textareaId} className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          rows={rows}
          className={cn(textareaVariants({ variant: effectiveVariant, size }), className)}
          aria-invalid={!!error}
          aria-describedby={error ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : undefined}
          {...props}
        />
        {error && (
          <p id={`${textareaId}-error`} className="text-sm text-danger-strong" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={`${textareaId}-helper`} className="text-sm text-gray-500 dark:text-gray-400">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = "Textarea";
